
import { compileApiQuery, stringifyParams, getParamsFromQueryString } from '../helpers';
import { onlyTheseKeys } from '../../../../util/params';
import { addDays } from '../../../../util/date';
import { HISTORY_QUERY_KEYS } from '../constants';

export const initialState = {
  endDate: null,
  error: null,
  excludePlaceOfInterest: null,
  keywords: null,
  latitude: null,
  location: null, // used in queryString not API
  longitude: null,
  page: 0,
  radiusKm: 0, // this is updated in actionTypes.UPDATE_DEFAULT_LOCATION_RADIUS to get state.
  startDate: null,
  tags: [],
  totalResults: 0,
  isLoading: false,
  results: [],
  apiQuery: null,
  searchIsStale: true,
  resetIsStale: true,
  previousSearchForLocation: null,
  isInitialSearch: true,
};

export const actionTypes = {
  ERROR: 'error',
  GEO_COMPLETE: 'geoComplete',
  LOADING_START: 'loadingStart',
  LOADING_STOP: 'loadingStop',
  PAGINATION: 'pagination',
  POP_STATE: 'popState',
  RESET: 'reset',
  SEARCH_COMPLETE: 'searchComplete',
  SEARCH: 'search',
  UPDATE_DEFAULT_LOCATION_RADIUS: 'updateDefaultLocationRadius',
  UPDATE_SEARCH_PARAM_AND_SEARCH: 'updateSearchParamAndSearch',
  UPDATE_SEARCH_PARAM: 'updateSearchParam',
};


export function areParamsInitialize(state) {
  const keys = HISTORY_QUERY_KEYS.concat(['page']); // we want reset to reset page as well
  return stringifyParams(onlyTheseKeys(initialState, keys))
    === stringifyParams(onlyTheseKeys(state, keys));
}

function areParamsTheSame(state, globals) {
  return `${compileApiQuery(state, globals)}${state.previousSearchForLocation}`
    === `${state.apiQuery}${state.location}`;
}

/**
 * If endDate is before or equal to start date set to next day
 */
function applyDateRules(state) {
  if (state.startDate && state.endDate) {
    if (state.startDate.getTime() >= state.endDate.getTime()) {
      return {
        ...state,
        endDate: addDays(1, new Date(state.startDate.getTime())),
      };
    }
  }

  return state;
}

export const reducer = (state = initialState, action) => {
  let mergedState;
  switch (action.type) {
    case actionTypes.ERROR:
      return { ...state, error: action.payload };

    case actionTypes.UPDATE_DEFAULT_LOCATION_RADIUS:
      return { ...state, radiusKm: action.payload.radiusKm };

    case actionTypes.LOADING_START:
      return { ...state, isLoading: true };

    case actionTypes.LOADING_STOP:
      return { ...state, isLoading: false };

    case actionTypes.PAGINATION:
      mergedState = {
        ...state,
        page: action.payload.page,
        searchIsStale: true,
        previousSearchForLocation: state.location,
      };
      return {
        ...mergedState,
        resetIsStale: areParamsInitialize(mergedState),
        apiQuery: compileApiQuery(mergedState, action.payload.globals),
      };

    case actionTypes.POP_STATE:
      mergedState = applyDateRules({
        ...state,
        ...onlyTheseKeys(initialState, HISTORY_QUERY_KEYS),
        ...getParamsFromQueryString(),
        searchIsStale: true,
        previousSearchForLocation: state.location,
      });
      return {
        ...mergedState,
        resetIsStale: areParamsInitialize(mergedState),
        apiQuery: compileApiQuery(mergedState, action.payload.globals),
      };

    case actionTypes.UPDATE_SEARCH_PARAM:
      mergedState = applyDateRules({
        ...state,
        [action.payload.key]: action.payload.value,
      });

      return {
        ...mergedState,
        resetIsStale: areParamsInitialize(mergedState),
        searchIsStale: areParamsTheSame(mergedState, action.payload.globals),
      };

    case actionTypes.UPDATE_SEARCH_PARAM_AND_SEARCH:
      mergedState = applyDateRules({
        ...state,
        [action.payload.key]: action.payload.value,
        page: 0,
        searchIsStale: true,
        previousSearchForLocation: state.location,
      });
      return {
        ...mergedState,
        resetIsStale: areParamsInitialize(mergedState),
        apiQuery: compileApiQuery(mergedState, action.payload.globals),
      };

    case actionTypes.RESET:
      mergedState = {
        ...state,
        ...onlyTheseKeys(initialState, HISTORY_QUERY_KEYS),
      };
      return {
        ...mergedState,
        searchIsStale: true,
        resetIsStale: true,
        previousSearchForLocation: mergedState.location,
        apiQuery: compileApiQuery({ ...mergedState }, action.payload.globals),
      };

    case actionTypes.SEARCH:
      mergedState = {
        ...state,
        searchIsStale: true,
        previousSearchForLocation: state.location,
      };

      if (action.payload.latitude !== undefined) mergedState.latitude = action.payload.latitude;
      if (action.payload.longitude !== undefined) mergedState.longitude = action.payload.longitude;
      // new searches via the filters want to reset,
      // initial searches don't if page is coming through from query
      if (action.payload.resetPage === true) mergedState.page = 0;

      return {
        ...mergedState,
        apiQuery: compileApiQuery({ ...mergedState }, action.payload.globals),
        isInitialSearch: action.payload.isInitialSearch,
      };

    case actionTypes.SEARCH_COMPLETE:
      return { ...state, ...action.payload };

    default:
      return state;
  }
};
